import { Box, Typography, CircularProgress } from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import React, { useContext, useEffect, useRef } from 'react';
import { Formik, FormikHelpers } from 'formik';
import { WhiteInput, WhiteLink } from './AppModals';
import FirebaseContext from '../../context/firebase';
import firebase from 'firebase/app';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UserContext from '../../context/user';
import SnackBarContext from '../../context/snackbar';
import ModalContext, { ConfirmationServiceContext } from '../../context/modal';
import { useHistory } from 'react-router-dom';
import useVerifyPhoneNumber from '../../hooks/useVerifyPhoneNumber';
import { SubmitButton } from '../Form';
import useUserApiRoutes from '../../hooks/api/useUserApiRoutes';
import useHandleError from '../../hooks/useHandleError';

type Props = {
  onVerify?: (credential: firebase.auth.AuthCredential | null) => void;
  // Send a code as soon as the form opens. Off by default: callers that
  // already sent one before opening this form (Settings > Profile) would
  // otherwise text the user twice.
  autoSend?: boolean;
};
interface MyFormValues {
  number1: string;
  number2: string;
  number3: string;
  number4: string;
  number5: string;
  number6: string;
}

const VerifyMobileModal = (props: Props) => {
  const { setDrawerOpen } = useContext(ModalContext);
  const { firebaseApp, currentUser, reloadCurrentUser } = useContext(FirebaseContext);
  const { user, saveUser } = useContext(UserContext);
  const history = useHistory();
  const { showConfirmationModal, setOpenId } = useContext(ConfirmationServiceContext);
  const { updateUser } = useUserApiRoutes();
  const handleError = useHandleError();

  const handleVerify = async (credential: firebase.auth.AuthCredential | null) => {
    try {
      if (firebaseApp) {
        if (credential) {
          if (currentUser) {
            // we are already signed in so just update the number for the current user
            await currentUser?.updatePhoneNumber(credential);
            await reloadCurrentUser();
          } else {
            await firebaseApp.auth().signInWithCredential(credential);
          }
        }
        const currentFirebaseEmail = currentUser?.email;
        if (user && user.email && user.email !== currentFirebaseEmail) {
          if (currentFirebaseEmail) {
            const res = await showConfirmationModal({
              title: 'Update your email',
              contentText: `You have previously signed up with another email (${currentFirebaseEmail}). Do you wish to update your email to ${user.email}?`,
            });
            if (res && res.confirmed === true) {
              // must pass the idToken to the route as we may have signed in with a new credential
              const idToken = await currentUser?.getIdToken();
              await updateUser({ email: user.email, uid: user?.uid }, idToken);
              saveUser({ email: user.email });
              setOpenId(undefined);
            }
          }
        }
      }
      setDrawerOpen(undefined);
      history.replace('/settings/edit-account');
    } catch (error) {
      handleError(error);
    }
  };

  const goEditNumber = () => {
    setDrawerOpen(undefined);
    history.push('/settings/edit-profile');
  };

  return (
    <Box>
      <Typography variant="h4">Verify Mobile #</Typography>
      {user?.phoneNumber ? (
        <Box mt={2}>
          <Typography variant="body2">Sending a code to {user.phoneNumber}</Typography>
        </Box>
      ) : null}
      <VerifyMobileForm onVerify={handleVerify} autoSend />
      <Box mt={2}>
        <WhiteLink
          href="#"
          onClick={(e) => {
            e.preventDefault();
            goEditNumber();
          }}
        >
          Entered the wrong number? Update it in your profile
        </WhiteLink>
      </Box>
    </Box>
  );
};

export const VerifyMobileForm = (props: Props) => {
  const classes = useStyles();
  const { firebaseApp } = useContext(FirebaseContext);
  const { showSnack } = useContext(SnackBarContext);
  const { user } = useContext(UserContext);
  const { verifyPhoneNumber, prepareVerifier } = useVerifyPhoneNumber();
  const { onVerify, autoSend } = props;

  // Get the reCAPTCHA handshake out of the way before the user asks for a
  // code, so the send isn't waiting on it.
  useEffect(() => {
    prepareVerifier();
  }, [prepareVerifier]);

  // Auto-send exactly once per number when the form opens — the effect's
  // dependencies churn as the user context updates, and each re-fire used to
  // send another SMS on top of manual resends.
  const autoSentTo = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (autoSend && firebaseApp && user?.phoneNumber && autoSentTo.current !== user.phoneNumber) {
      autoSentTo.current = user.phoneNumber;
      verifyPhoneNumber(user.phoneNumber, user?.email || undefined)
        .then(() => {
          showSnack('Please check your mobile for your verification code', 'success');
        })
        .catch(() => {
          showSnack('We could not send the code — tap Resend Verification Code below to try again.', 'error');
        });
    }
  }, [autoSend, firebaseApp, showSnack, user?.email, user?.phoneNumber, user, verifyPhoneNumber]);

  const initialValues: MyFormValues = {
    number1: '',
    number2: '',
    number3: '',
    number4: '',
    number5: '',
    number6: '',
  };
  const inputRefs = useRef<any[]>(Object.keys(initialValues).map(() => undefined)); //init to an array of undefineds

  const validate = (values: MyFormValues) => {
    const errors: Partial<MyFormValues> = {};
    (Object.keys(initialValues) as Array<keyof typeof initialValues>).forEach((k) => {
      if (!values[k]) {
        errors[k] = 'Required';
      }
    });
    return errors;
  };
  const verify = (values: MyFormValues, { setSubmitting, resetForm }: FormikHelpers<MyFormValues>) => {
    if (user?.firebase?.verificationId) {
      const fullCode = Object.values(values).join('');
      const credential = firebase.auth.PhoneAuthProvider.credential(user.firebase.verificationId, fullCode);
      if (onVerify) {
        onVerify(credential);
      }
      resetForm();
      setSubmitting(false);
    }
    // if (user?.firebase?.confirmationResult) {
    //   const fullCode = Object.values(values).join('');
    //   // return firebase.auth.PhoneAuthProvider.credential(verificationId, verificationCode);

    //   user.firebase.confirmationResult
    //     .confirm(fullCode)
    //     .then(async (result) => {
    //       // User signed in successfully which will trigger the onAuthStateChanged listener at the App.tsx level I.e. no need to do anything with the response
    //       // const user = result.user;
    //       setSubmitting(false);
    //       if (onVerify) {
    //         onVerify(result.credential);
    //       }
    //     })
    //     .catch((error) => {
    //       setSubmitting(false);
    //       showSnack(error, 'error');
    //     });
    // }
  };

  const fieldKeys = Object.keys(initialValues) as Array<keyof MyFormValues>;

  const focusChange = (index: number) => {
    if (index < fieldKeys.length - 1) {
      if (inputRefs.current[index + 1]) {
        inputRefs.current[index + 1].focus();
      }
    }
  };

  // Spread a pasted code (e.g. straight from the SMS) across the boxes,
  // starting at the one pasted into. Ignores any non-digits. Attached to the
  // native input via inputProps — MUI routes unknown TextField props to the
  // wrapper, so the box would otherwise just swallow the whole code and
  // truncate it to its single character.
  const spreadDigits = (
    raw: string,
    index: number,
    setFieldValue: (field: string, value: string) => void
  ): boolean => {
    const digits = raw.replace(/\D/g, '');
    if (!digits) return false;
    const usable = digits.slice(0, fieldKeys.length - index);
    usable.split('').forEach((digit, offset) => setFieldValue(fieldKeys[index + offset], digit));
    const lastFilled = Math.min(index + usable.length, fieldKeys.length - 1);
    inputRefs.current[lastFilled]?.focus();
    return true;
  };

  const handlePaste = (
    event: React.ClipboardEvent<HTMLInputElement>,
    index: number,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const pasted = event.clipboardData?.getData('text') || '';
    if (!pasted.replace(/\D/g, '')) return;
    event.preventDefault();
    event.stopPropagation();
    spreadDigits(pasted, index, setFieldValue);
  };

  // Autofill (and some mobile keyboards) can drop the whole code into one box,
  // bypassing maxLength — spread it rather than keeping a single digit.
  const handleInput = (
    event: React.FormEvent<HTMLInputElement>,
    index: number,
    setFieldValue: (field: string, value: string) => void
  ) => {
    const value = (event.target as HTMLInputElement).value || '';
    if (value.length > 1) {
      spreadDigits(value, index, setFieldValue);
      return;
    }
    focusChange(index);
  };

  const resendCode = (setSubmitting: (isSubmitting: boolean) => void) => {
    if (firebaseApp && user?.phoneNumber) {
      setSubmitting(true);
      verifyPhoneNumber(user?.phoneNumber, user?.email || undefined)
        .then(() => {
          showSnack('Verification code sent — please check your mobile', 'success');
        })
        .catch(() => {
          // handleError inside the hook has already shown the reason
        })
        .finally(() => {
          setSubmitting(false);
        });
    } else {
      showSnack('No mobile number defined', 'error');
    }
  };

  return (
    <Formik initialValues={initialValues} validate={validate} onSubmit={verify}>
      {({ handleSubmit, isSubmitting, setSubmitting, setFieldValue }) => (
        <form onSubmit={handleSubmit}>
          {fieldKeys.map((k, index) => {
            return (
              <WhiteInput
                key={k}
                inputRef={(input) => inputRefs.current.splice(index, 1, input)}
                type="text"
                maxLength={1}
                name={k}
                placeholder="*"
                inputProps={{
                  inputMode: 'numeric',
                  pattern: '[0-9]*',
                  // Lets iOS/Android offer the code straight from the SMS.
                  autoComplete: index === 0 ? 'one-time-code' : 'off',
                  onPaste: (event: React.ClipboardEvent<HTMLInputElement>) => handlePaste(event, index, setFieldValue),
                }}
                onInput={(event) => handleInput(event as React.FormEvent<HTMLInputElement>, index, setFieldValue)}
              />
            );
          })}
          <Box display="flex" marginTop={3}>
            <SubmitButton classes={{ root: classes.marginRight }}>
              {isSubmitting ? null : <ArrowForwardIcon />}
            </SubmitButton>
          </Box>
          <Box marginTop={4} marginBottom={1}>
            {isSubmitting ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              <WhiteLink href="#" onClick={() => resendCode(setSubmitting)}>
                Resend VerificationCode
              </WhiteLink>
            )}
          </Box>
        </form>
      )}
    </Formik>
  );
};

export default VerifyMobileModal;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    marginTop: {
      marginTop: theme.spacing(5),
    },
    marginRight: {
      marginRight: theme.spacing(2.25),
    },
  })
);
